import { useContext, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { logError } from '~/common/utils/log';
import { PromotionsModalsContext } from '~/domains/promotions/Modals/PromotionsModalsContext';
import { PromoCarouselLocation } from '~/domains/promotions/types';

import { markMissionCompletionSeen } from '../services/missions.api';
import { MissionStatus } from '../types/missions.types';
import { MISSIONS_QUERY_KEY_PREFIX } from '../constants/query';
import { reportMissionCompletedOpened } from '../analyticsEvents';

import { useGetMissionsData } from './useGetMissionsData';

const COMPLETED_MODAL_TYPE = 'MissionCompletedModal';

/**
 * Shows the "Mission Completed" modal once per completed assignment.
 *
 * Mounted once at the app root via the singleton `GamificationContextProvider`, so this is a
 * plain single-instance hook — no module-level coordination. It is driven off the
 * completed-missions REST list (so it works on a cold open); a `mission_progress` push simply
 * refetches that list and re-runs this effect.
 *
 * The server `completionSeenAt` field is the durable, cross-device source of truth.
 * `shownThisSession` (component-scoped) only prevents an immediate re-open in the window
 * between dismissal and the post-`/seen` refetch — it resets on reload, so a failed `/seen`
 * POST just re-shows on a future load rather than diverging permanently.
 */
export const useMissionCompletionModal = (): void => {
  const { openModalUnique, modals } = useContext(PromotionsModalsContext);
  const queryClient = useQueryClient();

  const { items } = useGetMissionsData(
    true,
    PromoCarouselLocation.PROMO_CENTER,
    MissionStatus.COMPLETED
  );

  const shownThisSession = useRef(new Set<string>());
  // Serializes the one-at-a-time drain within this single instance (survives the modals-dep
  // re-runs that fire while a modal is open, without cancelling the in-flight drain).
  const isProcessing = useRef(false);

  useEffect(() => {
    if (isProcessing.current) {
      return;
    }

    // Defer under any open modal: both the "one at a time" sequencer and the "don't supersede
    // an in-progress modal" guard — correct now because this reads the single global stack.
    if (modals.length > 0) {
      return;
    }

    const next = items.find(
      (mission) =>
        mission.status === MissionStatus.COMPLETED &&
        mission.completionSeenAt === null &&
        !shownThisSession.current.has(mission.assignmentId)
    );

    if (!next) {
      return;
    }

    const { assignmentId } = next;
    isProcessing.current = true;

    (async () => {
      try {
        reportMissionCompletedOpened({ missionAssignment: next, location: 'pusher' });
        // Resolves when the modal is dismissed.
        await openModalUnique({ type: COMPLETED_MODAL_TYPE, isClosing: true, assignmentId });
        shownThisSession.current.add(assignmentId);
        try {
          await markMissionCompletionSeen(assignmentId);
        } catch (error) {
          logError('Failed to mark mission completion seen', { assignmentId, error });
        }
        // Refetch so the list returns `completionSeenAt` set and the next pending mission shows.
        queryClient.invalidateQueries({ queryKey: [MISSIONS_QUERY_KEY_PREFIX] });
      } finally {
        isProcessing.current = false;
      }
    })();
  }, [items, modals, openModalUnique, queryClient]);
};
